"use client";

import { createContext, useContext, useState } from "react";
import { type StoreApi, useStore } from "zustand";
import { createPermitStore } from "./permitStore";
import type { PermitFormStore, WizardSettings } from "./types";

export const PermitStoreContext = createContext<StoreApi<PermitFormStore> | undefined>(undefined);

type PermitStoreProviderProps = {
	children: React.ReactNode;
	initialValues?: {
		wizardSettings: WizardSettings;
		formData?: Pick<PermitFormStore["formData"], "vehicles" | "email">;
		isParkingTimeStepForced?: boolean;
	};
};

export const PermitStoreProvider = ({ children, initialValues }: PermitStoreProviderProps) => {
	const [store] = useState(() =>
		createPermitStore({
			formData: initialValues?.formData,
			wizardSettings: initialValues?.wizardSettings,
			isParkingTimeStepForced: initialValues?.isParkingTimeStepForced,
		}),
	);

	return <PermitStoreContext.Provider value={store}>{children}</PermitStoreContext.Provider>;
};

const usePermitFormStore = <T,>(selector: (state: PermitFormStore) => T) => {
	const store = useContext(PermitStoreContext);
	if (!store) {
		throw new Error("usePermitFormStore must be used within PermitStoreProvider");
	}

	return useStore(store, selector);
};

export const usePermitFormData = () => usePermitFormStore((state) => state.formData);

export const usePermitSteps = () => usePermitFormStore((state) => state.steps);

export const usePermitActiveStep = () => usePermitFormStore((state) => state.activeStep);

export const usePermitError = () => usePermitFormStore((state) => state.error);

export const useIsProcessingPermit = () => usePermitFormStore((state) => state.isProcessingPermit);

export const usePaymentRetryError = () => usePermitFormStore((state) => state.paymentRetryError);

export const usePermitFormActions = () => usePermitFormStore((state) => state.actions);

export const usePermitWizardSettings = () => usePermitFormStore((state) => state.wizardSettings);
